import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useMe} from '../../../common/hooks/useMe'
import {convertImage} from '../../../common/utils/imageConverter'
import {IMAGE_FORMATS, type ImageFormat} from '../../../common/constants/converterFormats'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {downloadFile} from '../../../common/utils/downloadFile'
import ConverterExportDialog from '../converterShared/ConverterExportDialog'
import {CONVERTER_AD_FEATS} from '../converterShared/adFeatIcons'

interface ImageDownloadPanelProps {
  image: HTMLImageElement | null
  format: ImageFormat
  quality: number
  icoSizes: readonly number[]
}

const ImageDownloadPanel = ({image, format, quality, icoSizes}: ImageDownloadPanelProps) => {
  const {t} = useTranslation()
  const {me} = useMe()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const hasCredits = me !== null && me.credits > 0
  const fileExt = IMAGE_FORMATS.find((f) => f.id === format)?.fileExt ?? format
  const filename = `converted.${fileExt}`

  const generate = useCallback(
    () => (image ? convertImage(image, format, {quality, icoSizes}) : Promise.reject(new Error('No source image'))),
    [image, format, quality, icoSizes],
  )

  const handleClick = useCallback(async () => {
    if (!image) return
    trackEvent('image_converter_download_clicked', {has_credits: hasCredits, format})
    if (!hasCredits) {
      setDialogOpen(true)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const blob = await generate()
      await downloadFile(blob, filename)
      trackEvent('image_converter_export_completed', {size_bytes: blob.size, format, gated: false})
    } catch {
      setError(t('imageConverter.download.failed'))
    } finally {
      setBusy(false)
    }
  }, [image, hasCredits, format, generate, filename, t])

  const handleDialogClose = useCallback(() => setDialogOpen(false), [])

  const disabled = busy || !image

  return (
    <div className="imc-download">
      <button type="button" className="cvt-btn cvt-btn-primary" onClick={handleClick} disabled={disabled}>
        {busy ? t('imageConverter.download.busy') : t('imageConverter.download.cta')}
      </button>
      {error && <p className="cvt-error">{error}</p>}
      {!image && <p className="cvt-hint">{t('imageConverter.download.needImage')}</p>}

      {dialogOpen && (
        <ConverterExportDialog
          namespace="imageConverter"
          breadcrumbCategory="image-converter"
          analyticsSurface="image_converter"
          startedEvent="image_converter_export_started"
          completedEvent="image_converter_export_completed"
          filename={filename}
          showAd
          adFeats={CONVERTER_AD_FEATS}
          adCtaTo="/free"
          onGenerate={generate}
          onClose={handleDialogClose}
        />
      )}
    </div>
  )
}

export default ImageDownloadPanel
